import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
  query,
  where,
  deleteDoc,
  writeBatch,
  deleteField,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import {
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
  Plus,
  X,
  Settings,
  Tag,
  ListVideo,
  Lock,
  Unlock,
} from "lucide-react";
import EpisodeManager from "./EpisodeManager";
import FormattedDescription from "./FormattedDescription";
import SeriesDataManager from "./SeriesDataManager";

const SeriesManager = ({ onManageVideos }) => {
  const [seriesList, setSeriesList] = useState([]);
  const [newSeries, setNewSeries] = useState({
    id: "",
    title: "",
    shortDescription: "",
    longDescription: "",
    releaseDate: "",
    tags: [],
    thumbnail: null,
    password: "",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [idTaken, setIdTaken] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [sortBy, setSortBy] = useState("order"); // "tag" or "order"
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("all");

  const fetchSeries = async () => {
    try {
      const seriesCollection = collection(db, "content", "main", "series");
      const seriesSnapshot = await getDocs(seriesCollection);
      const seriesData = seriesSnapshot.docs.map((doc) => ({
        docId: doc.id,
        id: doc.id,
        ...doc.data(),
      }));

      // Extract unique tags
      const tags = new Set();
      seriesData.forEach((series) => {
        if (series.tags) {
          series.tags.forEach((tag) => tags.add(tag));
        }
      });
      setAvailableTags(Array.from(tags).sort());

      // Sort based on current sorting preference
      const sortedSeries = sortSeriesList(seriesData, sortBy);
      setSeriesList(sortedSeries);
    } catch (error) {
      console.error("Error fetching series:", error);
    }
  };

  const sortSeriesList = (list, sortType) => {
    if (sortType === "tag") {
      return stableSort(list, (a, b) => {
        const tagA = (a.tags && a.tags[0]?.toLowerCase()) || "";
        const tagB = (b.tags && b.tags[0]?.toLowerCase()) || "";
        return tagA.localeCompare(tagB);
      });
    } else if (sortType === "order") {
      return stableSort(list, (a, b) => (a.order || 0) - (b.order || 0));
    }
    return list;
  };

  const stableSort = (arr, compare) => {
    const arrCopy = [...arr];
    return arrCopy.sort(compare);
  };

  const moveSeries = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sortedFilteredSeries.length) return;

    try {
      const updatedSeries = [...sortedFilteredSeries];
      const [movedSeries] = updatedSeries.splice(fromIndex, 1);
      updatedSeries.splice(toIndex, 0, movedSeries);

      const batch = writeBatch(db);

      updatedSeries.forEach((series, index) => {
        const seriesRef = doc(db, "content", "main", "series", series.docId);
        batch.update(seriesRef, {
          order: index,
        });
      });

      await batch.commit();

      fetchSeries();
      updateLastUpdated();
    } catch (error) {
      console.error("Error updating series order:", error);
    }
  };

  // Filter series based on selected tag
  const filteredSeries =
    selectedTag === "all"
      ? seriesList
      : seriesList.filter((series) => series.tags?.includes(selectedTag));

  // Now, apply the sort after filtering
  const sortedFilteredSeries = sortSeriesList(filteredSeries, sortBy);

  // Check if the entered ID is already taken
  const checkIfIdTaken = async (id) => {
    const seriesRef = collection(db, "content", "main", "series");
    const q = query(seriesRef, where("id", "==", id));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty; // Returns true if ID is taken, false if available
  };

  // In your handleInputChange function, modify to keep thumbnail state properly
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "id") {
      setNewSeries({ ...newSeries, [name]: value.toLowerCase() });
      setIdTaken(false);
    } else if (name === "tags") {
      setNewSeries({
        ...newSeries,
        tags: value.split(",").map((tag) => tag.trim()),
      }); // Ensure array format
    } else {
      setNewSeries({ ...newSeries, [name]: value });
    }
  };

  // Handle file changes for thumbnail
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file); // Create a temporary URL for preview
      setNewSeries({
        ...newSeries,
        thumbnail: file, // Keep the file for uploading
        previewThumbnail: previewUrl, // Store the preview URL for display
        // Keep existingThumbnail (don't remove it from state)
      });
    }
  };

  // Handle add series logic (ensures the ID is unique)
  const handleAddSeries = async () => {
    if (!newSeries.title || !newSeries.id || !newSeries.releaseDate) {
      alert("Please fill in the required fields.");
      return;
    }

    // Check if the entered ID is taken
    const idExists = await checkIfIdTaken(newSeries.id);
    if (idExists) {
      setIdTaken(true);
      return;
    }

    try {
      let thumbnailUrl = "";

      // If thumbnail exists, upload it to Firebase Storage with a UUID filename
      if (newSeries.thumbnail) {
        const thumbnailFileName = uuidv4(); // Generate a unique filename
        const thumbnailRef = ref(storage, `thumbnails/${thumbnailFileName}`);

        // Upload the thumbnail with the correct content type
        const metadata = {
          contentType: newSeries.thumbnail.type, // Ensure the correct image type
        };

        await uploadBytes(thumbnailRef, newSeries.thumbnail, metadata);

        // Get the URL of the uploaded thumbnail
        thumbnailUrl = await getDownloadURL(thumbnailRef);
      }

      // Prepare series data without password field initially
      const seriesData = {
        id: newSeries.id,
        title: newSeries.title,
        releaseDate: newSeries.releaseDate,
        thumbnail: thumbnailUrl,
        tags: Array.isArray(newSeries.tags)
          ? newSeries.tags.map((tag) => tag.trim())
          : [], // Ensure it's an array and trim each tag
        shortDescription: newSeries.shortDescription,
        longDescription: newSeries.longDescription,
        order: seriesList.length,
      };

      // Only add password field if it's not empty
      if (newSeries.password && newSeries.password.trim() !== "") {
        seriesData.password = newSeries.password.trim();
      }
      // No else clause - we simply don't include the field if there's no password

      // Ensure that the 'content' document exists, or create it
      const contentDocRef = doc(db, "content", "main");

      // Add series document under the 'content' -> 'series' collection
      const newSeriesRef = doc(collection(contentDocRef, "series"));
      await setDoc(newSeriesRef, seriesData);

      // Reset form and show success
      resetForm();
      setIsDropdownOpen(false);
      alert("Series added successfully.");
      fetchSeries();
      updateLastUpdated();
    } catch (error) {
      console.error("Error adding series:", error);
      alert("Error adding series.");
    }
  };

  // Modify how you set the editing series state
  const handleEditClick = (series) => {
    setEditingSeries(series);
    setNewSeries({
      ...series,
      password: series.password || "", // Ensure password is never undefined
      thumbnail: null, // Set to null as we're not uploading a new file yet
      existingThumbnail: series.thumbnail, // Store the existing thumbnail URL
      previewThumbnail: series.thumbnail, // Set the preview to the existing thumbnail
    });
    setIsDropdownOpen(true);
  };
  // Handle edit series logic (ensure ID is not changed to a taken one)
  const handleEditSeries = async () => {
    if (!newSeries.title || !newSeries.id || !newSeries.releaseDate) {
      alert("Please fill in the required fields.");
      return;
    }

    // Check if the entered ID is taken (for edit as well)
    const idExists = await checkIfIdTaken(newSeries.id);
    if (idExists && newSeries.id !== editingSeries.id) {
      setIdTaken(true);
      return;
    }

    try {
      let thumbnailUrl = newSeries.existingThumbnail; // Default to existing thumbnail

      // If a new thumbnail is selected, upload it
      if (newSeries.thumbnail) {
        const thumbnailFileName = uuidv4(); // Generate a unique filename
        const thumbnailRef = ref(storage, `thumbnails/${thumbnailFileName}`);

        // Upload the thumbnail with the correct content type
        const metadata = {
          contentType: newSeries.thumbnail.type, // Ensure the correct image type
        };

        await uploadBytes(thumbnailRef, newSeries.thumbnail, metadata);

        // Get the URL of the uploaded thumbnail
        thumbnailUrl = await getDownloadURL(thumbnailRef);
      }

      // Prepare update data
      const updateData = {
        title: newSeries.title,
        releaseDate: newSeries.releaseDate,
        shortDescription: newSeries.shortDescription,
        longDescription: newSeries.longDescription,
        tags: Array.isArray(newSeries.tags)
          ? newSeries.tags.map((tag) => tag.trim())
          : [], // Ensure it's an array and trim each tag
        thumbnail: thumbnailUrl, // Use either the new upload or existing thumbnail
      };

      const seriesRef = doc(
        db,
        "content",
        "main",
        "series",
        editingSeries.docId,
      );

      // If password exists and has content, update it
      if (newSeries.password && newSeries.password.trim() !== "") {
        updateData.password = newSeries.password.trim();
        await updateDoc(seriesRef, updateData);
      } else {
        // If password is empty, use two operations:
        // 1. Update the document with all fields except password
        await updateDoc(seriesRef, updateData);

        // 2. If there was a password before, remove the field completely
        if (editingSeries.password) {
          await updateDoc(seriesRef, {
            password: deleteField(),
          });
        }
      }

      resetForm();
      fetchSeries();
      updateLastUpdated();
    } catch (error) {
      console.error("Error editing series:", error);
      alert("Error editing series.");
    }
  };

  // Handle delete series logic
  const handleDeleteSeries = async (seriesId) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this series?",
    );
    if (confirmation) {
      try {
        // Query the Firestore collection to find the document with the matching 'id' field
        const seriesRefQuery = query(
          collection(db, "content", "main", "series"),
          where("id", "==", seriesId), // Match by 'id' field in the document
        );

        // Execute the query
        const querySnapshot = await getDocs(seriesRefQuery);

        if (!querySnapshot.empty) {
          // Get the document reference of the matched series
          const seriesDoc = querySnapshot.docs[0];
          const seriesRef = doc(db, "content", "main", "series", seriesDoc.id); // Firestore document ID

          // Delete the series from Firestore
          await deleteDoc(seriesRef);

          // Remove the deleted series from the local state (seriesList)
          setSeriesList((prevSeries) =>
            prevSeries.filter((series) => series.id !== seriesId),
          );

          alert("Series deleted successfully.");
        } else {
          alert("Series not found.");
        }
      } catch (error) {
        console.error("Error deleting series:", error);
        alert("Error deleting series.");
      }
    }
  };

  // Update `lastUpdated` in content metadata
  const updateLastUpdated = async () => {
    try {
      const docRef = doc(db, "content", "main");
      await updateDoc(docRef, {
        lastUpdated: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating lastUpdated:", error);
    }
  };

  // Reset form fields
  const resetForm = () => {
    setNewSeries({
      id: "",
      title: "",
      longDescription: "",
      shortDescription: "",
      thumbnail: null,
      previewThumbnail: null,
      existingThumbnail: null,
      releaseDate: "",
      tags: [],
      password: "",
    });
    setIdTaken(false);
    setEditingSeries(null); // Reset editing series
  };

  useEffect(() => {
    fetchSeries();
  }, []);

  useEffect(() => {
    fetchSeries();
  }, [sortBy]);

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h1 className="text-2xl font-bold mb-4 md:mb-0">Series Manager</h1>
        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
          <SeriesDataManager seriesList={seriesList} onComplete={fetchSeries} />{" "}
          <button
            onClick={() => {
              if (!isDropdownOpen) {
                resetForm();
              }
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {isDropdownOpen ? <X size={20} /> : <Plus size={20} />}
            {isDropdownOpen ? "Cancel" : "Add New Series"}
          </button>
          <div className="flex gap-2">
            <label className="bg-gray-700 text-white rounded-lg px-4 py-2">
              Sorted By Order
            </label>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="bg-gray-700 text-white rounded-lg px-4 py-2"
            >
              <option value="all">All Tags</option>
              {availableTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Add/Edit Series Form */}
      {isDropdownOpen && (
        <div className="bg-gray-800 p-6 rounded-lg mb-6 shadow-lg">
          <h3 className="text-xl font-bold mb-4">
            {editingSeries ? "Edit Series" : "Add New Series"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="id"
              value={newSeries.id}
              onChange={handleInputChange}
              placeholder="Series ID (lowercase), Will be in a URL E.g. site.com/series/series-id"
              className={`w-full p-3 bg-gray-700 rounded-lg ${
                idTaken ? "border-red-500 border-2" : ""
              }`}
            />
            <input
              type="text"
              name="title"
              value={newSeries.title}
              onChange={handleInputChange}
              placeholder="Series Title"
              className="w-full p-3 bg-gray-700 rounded-lg"
            />
            <textarea
              name="shortDescription"
              value={newSeries.shortDescription}
              onChange={handleInputChange}
              placeholder="Short Description"
              className="w-full p-3 bg-gray-700 rounded-lg"
              rows="2"
            />
            <textarea
              name="longDescription"
              value={newSeries.longDescription}
              onChange={handleInputChange}
              placeholder="Long Description"
              className="w-full p-3 bg-gray-700 rounded-lg"
              rows="2"
            />
            <input
              type="text"
              name="tags"
              value={newSeries.tags}
              onChange={handleInputChange}
              placeholder="Tags (comma separated)"
              className="w-full p-3 bg-gray-700 rounded-lg"
            />
            <input
              type="date"
              name="releaseDate"
              value={newSeries.releaseDate}
              onChange={handleInputChange}
              className="w-full p-3 bg-gray-700 rounded-lg"
            />

            {/* Password Field */}
            <div className="flex items-center">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  {newSeries.password ? (
                    <Lock size={16} />
                  ) : (
                    <Unlock size={16} />
                  )}
                </div>
                <input
                  type="text"
                  name="password"
                  value={newSeries.password || ""}
                  onChange={handleInputChange}
                  placeholder="Password (leave empty for no password)"
                  className="w-full p-3 pl-10 bg-gray-700 rounded-lg"
                />
              </div>
            </div>

            {/* Thumbnail Upload Section */}
            <div className="col-span-2">
              <label className="block mb-2">
                Thumbnail Upload (1280x720 .jpg or .png)
              </label>

              {/* Show thumbnail preview with proper fallback chain */}
              {newSeries.previewThumbnail ? (
                <div className="mb-4">
                  <img
                    src={newSeries.previewThumbnail}
                    alt="Current Thumbnail"
                    className="w-48 h-auto rounded-lg border border-gray-600 shadow-md"
                  />
                </div>
              ) : newSeries.existingThumbnail ? (
                <div className="mb-4">
                  <img
                    src={newSeries.existingThumbnail}
                    alt="Current Thumbnail"
                    className="w-48 h-auto rounded-lg border border-gray-600 shadow-md"
                  />
                </div>
              ) : null}

              <input
                type="file"
                onChange={handleFileChange}
                className="w-full p-3"
                accept=".jpg,.jpeg,.png,.gif"
              />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={editingSeries ? handleEditSeries : handleAddSeries}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
            >
              {editingSeries ? "Save Changes" : "Add Series"}
            </button>
          </div>
        </div>
      )}

      {/* Series List */}
      <div className="space-y-4">
        {filteredSeries.map((series, index) => (
          <div
            key={series.id}
            className="bg-gray-800 rounded-lg p-4 shadow-lg transition-all hover:shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold">
                  {series.title}
                  {series.password && (
                    <span className="ml-2 inline-flex items-center">
                      <Lock size={16} className="text-yellow-500" />
                      <span className="text-xs ml-1 text-yellow-500">
                        Password Protected
                      </span>
                    </span>
                  )}
                </h3>
                <div className="text-gray-400 text-sm mt-2">
                  <span className="font-semibold">Short Description:</span>
                  <FormattedDescription text={series.shortDescription} />
                </div>
                <div className="text-gray-400 text-sm mt-2">
                  <span className="font-semibold">Long Description:</span>
                  <FormattedDescription text={series.longDescription} />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <div className="text-gray-400 text-sm"> Tags: </div>
                  {series.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-700 text-sm px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {sortBy === "order" && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveSeries(index, index - 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <ChevronUp size={20} />
                    </button>
                    <button
                      onClick={() => moveSeries(index, index + 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <ChevronDown size={20} />
                    </button>
                  </div>
                )}
                <button
                  onClick={() =>
                    setSelectedSeries(
                      selectedSeries === series.id ? null : series.id,
                    )
                  }
                  className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <ListVideo size={20} />
                </button>
                {selectedSeries !== series.id && (
                  <>
                    <button
                      onClick={() => {
                        handleEditClick(series);
                      }}
                      className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => handleDeleteSeries(series.id)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </>
                )}
              </div>
            </div>
            {selectedSeries === series.id && (
              <div className="mt-4 border-t border-gray-700 pt-4">
                <EpisodeManager
                  seriesId={series.docId}
                  seriesTitle={series.title}
                  seriesThumbnail={series.thumbnail}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeriesManager;
